//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate } from 'react-i18next';

import LatLngUtils from 'common/utils/LatLngUtils';
import _ from 'lodash';
import { MarkerType, FitToBoundStrategyEnum } from 'common/enums/MapEnum';
import defaultTheme from './mapAutoFit.scss';
import { OutlineCheckbox } from 'common/components/core/form/Checkbox';

const MIN_AUTOFOCUS_TIME = 5 * 60 * 1000; //5 mins

export const MapWithFitToBoundFactory = GoogleMap => {
    class MapWithFitToBounds extends React.Component<
        $FlowFixMeProps,
        $FlowFixMeState
    > {
        constructor(props) {
            super(props);
            this.state = {
                autoFitToBound: props.enableFitToBounds
            };
            this.autoFit = true;
        }

        static defaultProps = {
            fitToBoundStrategy: FitToBoundStrategyEnum.NEXT_RENDER
        };

        componentDidUpdate(prevProps) {
            if (
                this.state.autoFitToBound ||
                this.autoFit ||
                this.props.forceFitBounds
            ) {
                const fitToBoundStrategy = this.getFitToBoundStrategy();
                if (fitToBoundStrategy === FitToBoundStrategyEnum.NEXT_RENDER) {
                    this.fitToBound();
                } else if (
                    fitToBoundStrategy ===
                    FitToBoundStrategyEnum.ONBOUNDS_CHANGE
                ) {
                    const boundsChanged = this.hasBoundsChanged(
                        this.props,
                        prevProps
                    );
                    if (boundsChanged || !this.allSelectedElementsVisible()) {
                        this.fitToBound();
                    }
                }
            }
        }

        allSelectedElementsVisible = () => {
            const { selectedElements } = this.props;
            const { mapInstance } = this.state;

            let areAllElementsVisible = true;
            selectedElements &&
                mapInstance &&
                selectedElements.some(elementBounds => {
                    if (!mapInstance.getBounds().contains(elementBounds)) {
                        areAllElementsVisible = false;
                        return true;
                    }
                });
            return areAllElementsVisible;
        };

        getMapInstance = () => {
            return this.state.mapInstance;
        };

        getFitToBoundStrategy = () => {
            return this.props.fitToBoundStrategy;
        };

        hasBoundsChanged = (props, prevProps) => {
            const bounds = this.getBounds(props);
            const prevBounds = this.getBounds(prevProps);
            return !bounds.equals(prevBounds);
        };

        toggleAutoFitToBound = () => {
            this.setState(
                {
                    autoFitToBound: !this.state.autoFitToBound
                },
                () => {
                    this.autoFit = this.state.autoFitToBound;
                }
            );
        };

        enableAutoFitToBound = () => {
            if (!this.state.autoFitToBound) {
                this.toggleAutoFitToBound();
            }
        };

        disableAutoFitToBound = () => {
            if (this.state.autoFitToBound) {
                this.toggleAutoFitToBound();
            }
        };

        onZoomOrDrag = () => {
            if (this.autoFit) {
                this.autoFit = false;
                return;
            } else {
                this.setState({
                    autoFitToBound: false
                });
            }
            if (this.userInactivityTimeout) {
                clearTimeout(this.userInactivityTimeout);
            }
            if (!this.props.disableInactivityTimeout) {
                this.userInactivityTimeout = setTimeout(() => {
                    this.setState(
                        {
                            autoFitToBound: true
                        },
                        () => {
                            this.autoFit = true;
                        }
                    );
                }, MIN_AUTOFOCUS_TIME);
            }
        };

        onMapLoad = ({ map, maps }) => {
            this.setState({
                mapInstance: map
            });
            if ('onGoogleApiLoaded' in this.props) {
                this.props.onGoogleApiLoaded({
                    map,
                    maps
                });
            }
        };

        getLatLngs = props => {
            // Sometimes children is one element, not array
            // So making them an array below
            return _.flattenDeep([props.children])
                .filter(child => {
                    return (
                        child &&
                        'lat' in child.props &&
                        'lng' in child.props &&
                        !child.props.ignoreForAutofit &&
                        !child.props.isArrow
                    );
                })
                .map(child => {
                    return {
                        lat: child.props.lat,
                        lng: child.props.lng
                    };
                });
        };

        getBounds = props => {
            var bounds;
            if (props.getBounds) {
                bounds = props.getBounds();
            } else if (props.bounds) {
                bounds = props.bounds;
            } else {
                var currentLatLngs = this.getLatLngs(props);
                bounds = LatLngUtils.getBoundsFromLatLngs(currentLatLngs);
            }
            return bounds;
        };

        fitBoundsMapInstance(bounds) {
            return this.state.mapInstance?.fitBounds(bounds);
        }

        fitToBound = () => {
            const { onFitToBounds } = this.props;
            if (this.state.mapInstance) {
                const bounds = this.getBounds(this.props);
                if (!bounds.isEmpty()) {
                    this.state.mapInstance.fitBounds(bounds);
                    if (this.autoFit) {
                        this.autoFit = false;
                    }
                    onFitToBounds && onFitToBounds();
                }
            }
        };

        distanceToMouse = (markerPos, mousePos, markerProps) => {
            if (markerProps.markerType === MarkerType.NON_HOVERABLE) {
                return Number.MAX_VALUE;
            }
            const { x, y } = markerPos;
            const distanceKoef = 1;

            // it's just a simple example, you can tweak distance function as you wish
            return (
                distanceKoef *
                Math.sqrt(
                    (x - mousePos.x) * (x - mousePos.x) +
                        (y - mousePos.y) * (y - mousePos.y)
                )
            );
        };

        renderAutoFitCheckbox() {
            var { theme, hideAutoFocus, t } = this.props;
            if (!hideAutoFocus) {
                return (
                    <span className={theme.autoFocusWrap}>
                        <OutlineCheckbox
                            theme={theme}
                            checked={this.state.autoFitToBound}
                            label={t('autoFocus')}
                            onChange={this.toggleAutoFitToBound}
                        />
                    </span>
                );
            }
        }

        onZoomAnimationEnd = e => {
            const { onZoomAnimationEnd } = this.props;
            onZoomAnimationEnd && onZoomAnimationEnd();
            this.onZoomOrDrag(e);
        };

        onDrag = e => {
            const { onDrag } = this.props;
            onDrag && onDrag();
            this.onZoomOrDrag(e);
        };

        render() {
            const { theme, distanceToMouse } = this.props;
            return (
                <div className={theme.autoFitBoundMapWrap}>
                    <GoogleMap
                        {...this.props}
                        yesIWantToUseGoogleMapApiInternals
                        onGoogleApiLoaded={this.onMapLoad}
                        onZoomAnimationEnd={this.onZoomAnimationEnd}
                        distanceToMouse={
                            distanceToMouse
                                ? distanceToMouse
                                : this.distanceToMouse
                        }
                        onDrag={this.onDrag}
                    />
                    {this.renderAutoFitCheckbox()}
                </div>
            );
        }
    }
    return compose(
        themr('MapWithFitToBounds', defaultTheme, { withRef: true }),
        translate('translations', { withRef: true })
    )(MapWithFitToBounds);
};

export default MapWithFitToBoundFactory;
